import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../AllProducts/AllProducts.css";
import Navbar from "../../components/shared/Navbar/Navbar";
import Footer from "../../components/shared/Footer/Footer";
import BoardsDetailHero from "../../components/pages/AllProducts/Boards/BoardsDetailHero/BoardsDetailHero";
import BoardsDetailTabs from "../../components/pages/AllProducts/Boards/BoardsDetailTabs/BoardsDetailTabs";
import BoardsDetailKeyFeatures from "../../components/pages/AllProducts/Boards/BoardsDetailKeyFeatures/BoardsDetailKeyFeatures";
import BoardsDetailTabPanel from "../../components/pages/AllProducts/Boards/BoardsDetailTabPanel/BoardsDetailTabPanel";
import BoardsDetailsSpecifications from "../../components/pages/AllProducts/Boards/BoardsDetailsSpecifications/BoardsDetailsSpecifications";
import BoardsDetailsRecommendations from "../../components/pages/AllProducts/Boards/BoardsDetailsRecommendations/BoardsDetailsRecommendations";
import BoardsDetailsSustainability from "../../components/pages/AllProducts/Boards/BoardsDetailsSustainability/BoardsDetailsSustainability";
import SolutionDetailsDownloads from "../../components/pages/SolutionDetails/SolutionDetailsDownloads/SolutionDetailsDownloads";
import BoardsDetailExploreSlider from "../../components/pages/AllProducts/Boards/BoardsDetailExploreSlider/BoardsDetailExploreSlider";
import BoardsDetailDisclaimer from "../../components/pages/AllProducts/Boards/BoardsDetailDisclaimer/BoardsDetailDisclaimer";
import { getBoardDetailById } from "../../data/boardsDetailData";
import AllSolutionsRequestNewProject from "../../components/pages/AllSolutions/AllSolutionsRequestNewProject/AllSolutionsRequestNewProject";
import { getBoardById, getBoards } from "../../api/boards";
import { filterPdfRowsByProductSlug, mapPublicProductPdfs, pickPublicPdfArray } from "../../utils/productPdfs";

const normalizeBoardKey = (value) =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[\s_-]+/g, "");

const toFeatureTextList = (items) =>
  Array.isArray(items)
    ? items
        .map((item) => (typeof item === "string" ? item : item?.text || ""))
        .filter(Boolean)
    : [];

const DEFAULT_STATIC_HERO = getBoardDetailById("regular")?.heroImage || "";

const BoardsDetail = () => {
  const { boardId } = useParams();
  const { i18n } = useTranslation();
  const fallbackBoard = getBoardDetailById(boardId);
  const [apiBoard, setApiBoard] = useState(null);
  const [listMatch, setListMatch] = useState(null);
  const [resolved, setResolved] = useState(Boolean(fallbackBoard));
  const [activeTab, setActiveTab] = useState("features");
  const sectionRefs = useRef({
    features: null,
    specifications: null,
    recommendations: null,
    sustainability: null,
    downloads: null,
  });

  useEffect(() => {
    let isMounted = true;
    setResolved(Boolean(fallbackBoard));

    const loadBoard = async () => {
      const language = String(i18n.language || "en").toLowerCase();
      try {
        const data = await getBoards({ language });
        const list = Array.isArray(data) ? data : [];
        const idKey = normalizeBoardKey(boardId);
        const matched = list.find((item) => {
          const candidates = [
            item?.boardType,
            item?.type,
            item?.name,
            item?.title,
            item?.slug,
            String(item?.id ?? ""),
          ];
          return candidates.some((candidate) => normalizeBoardKey(candidate) === idKey);
        });
        if (isMounted) {
          setListMatch(matched || null);
        }

        if (matched?.id != null) {
          try {
            const detail = await getBoardById({ id: matched.id, language });
            if (isMounted) {
              setApiBoard(detail || matched);
              setResolved(true);
            }
            return;
          } catch {
            /* fall through */
          }
        }

        const numericId = Number(boardId);
        if (!matched && Number.isFinite(numericId)) {
          try {
            const detail = await getBoardById({ id: numericId, language });
            if (isMounted) {
              setApiBoard(detail || null);
              setResolved(true);
            }
            return;
          } catch {
            /* fall through */
          }
        }

        if (isMounted) {
          setApiBoard(matched || null);
          setResolved(true);
        }
      } catch (_error) {
        if (isMounted) {
          setApiBoard(null);
          setListMatch(null);
          setResolved(true);
        }
      }
    };

    loadBoard();
    return () => {
      isMounted = false;
    };
  }, [boardId, i18n.language, fallbackBoard]);

  const board = useMemo(() => {
    if (!fallbackBoard && !apiBoard) return null;

    const boardPdfHints = [
      boardId,
      apiBoard?.boardType,
      apiBoard?.type,
      listMatch?.boardType,
      listMatch?.type,
      fallbackBoard?.id,
    ];

    const mergedBoardPdfs = (...sources) =>
      mapPublicProductPdfs(filterPdfRowsByProductSlug(pickPublicPdfArray(...sources), ...boardPdfHints));

    if (!apiBoard) return fallbackBoard ? { ...fallbackBoard, pdfs: mergedBoardPdfs(fallbackBoard, listMatch) } : null;

    if (!fallbackBoard) {
      return {
        id: boardId,
        heroTag: "GYPSUM BOARD",
        heroTitle: apiBoard?.name || apiBoard?.title || "Board",
        heroDescription: apiBoard?.heroDescription || "",
        heroDescriptionMore: "",
        heroImage: DEFAULT_STATIC_HERO,
        keyFeatures: toFeatureTextList(apiBoard?.keyFeatures),
        specificationsDescription:
          apiBoard?.specificationsDescription || apiBoard?.heroDescription || "",
        specificationSections: apiBoard?.specificationSections || [],
        recommendationApplications: apiBoard?.recommendationApplications || [],
        recommendationAccordionItems: apiBoard?.recommendationAccordionItems || [],
        recommendationsIntro: apiBoard?.recommendationsIntro || "",
        sustainabilityCards: apiBoard?.sustainabilityCards || [],
        sustainabilityStorageItems: apiBoard?.sustainabilityStorageItems || [],
        pdfs: mergedBoardPdfs(apiBoard, listMatch),
      };
    }

    return {
      ...fallbackBoard,
      heroTitle: apiBoard?.name || apiBoard?.title || fallbackBoard.heroTitle,
      heroDescription: apiBoard?.heroDescription || fallbackBoard.heroDescription,
      specificationsDescription:
        apiBoard?.specificationsDescription ||
        apiBoard?.heroDescription ||
        fallbackBoard.specificationsDescription ||
        fallbackBoard.heroDescription,
      keyFeatures: toFeatureTextList(apiBoard?.keyFeatures).length
        ? toFeatureTextList(apiBoard?.keyFeatures)
        : fallbackBoard.keyFeatures,
      specificationSections:
        Array.isArray(apiBoard?.specificationSections) && apiBoard.specificationSections.length
          ? apiBoard.specificationSections
          : fallbackBoard.specificationSections,
      recommendationApplications:
        Array.isArray(apiBoard?.recommendationApplications) && apiBoard.recommendationApplications.length
          ? apiBoard.recommendationApplications
          : fallbackBoard.recommendationApplications,
      recommendationAccordionItems:
        Array.isArray(apiBoard?.recommendationAccordionItems) && apiBoard.recommendationAccordionItems.length
          ? apiBoard.recommendationAccordionItems
          : fallbackBoard.recommendationAccordionItems,
      recommendationsIntro:
        apiBoard?.recommendationsIntro || fallbackBoard.recommendationsIntro,
      sustainabilityCards:
        Array.isArray(apiBoard?.sustainabilityCards) && apiBoard.sustainabilityCards.length
          ? apiBoard.sustainabilityCards
          : fallbackBoard.sustainabilityCards,
      sustainabilityStorageItems:
        Array.isArray(apiBoard?.sustainabilityStorageItems) && apiBoard.sustainabilityStorageItems.length
          ? apiBoard.sustainabilityStorageItems
          : fallbackBoard.sustainabilityStorageItems,
      heroImage: fallbackBoard.heroImage,
      pdfs: mergedBoardPdfs(apiBoard, listMatch, fallbackBoard),
    };
  }, [apiBoard, boardId, fallbackBoard, listMatch]);

  if (!board && !resolved) {
    return null;
  }

  if (!board) {
    return <Navigate to="/products/boards" replace />;
  }

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);

    const targetSection = sectionRefs.current[tabId];
    if (!targetSection) {
      return;
    }

    const fixedHeaderOffset = 100;
    const sectionTop = targetSection.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({
      top: sectionTop - fixedHeaderOffset,
      behavior: "smooth",
    });
  };

  const handleHeroReadMore = () => {
    const targetSection = sectionRefs.current.features;
    if (!targetSection) return;

    const fixedHeaderOffset = 100;
    const sectionTop = targetSection.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top: sectionTop - fixedHeaderOffset,
      behavior: "smooth",
    });
  };

  return (
    <div className="products-page">
      <div className="products-page-content-guide">
        <Navbar theme="dark" />
        <BoardsDetailHero board={board} onReadMore={handleHeroReadMore} />
        <BoardsDetailTabs activeTab={activeTab} onTabChange={handleTabChange} />

        <section ref={(node) => (sectionRefs.current.features = node)}>
          <BoardsDetailKeyFeatures features={board.keyFeatures} />
        </section>

        <section ref={(node) => (sectionRefs.current.specifications = node)}>
          <BoardsDetailsSpecifications board={board} />
        </section>

        <section ref={(node) => (sectionRefs.current.recommendations = node)}>
          <BoardsDetailsRecommendations board={board} />
        </section>

        <section ref={(node) => (sectionRefs.current.sustainability = node)}>
          <BoardsDetailsSustainability board={board} />
        </section>

        <section ref={(node) => (sectionRefs.current.downloads = node)}>
          <SolutionDetailsDownloads
            items={(board.pdfs || []).map((p) => ({
              id: p.id,
              title: p.title,
              href: p.href,
            }))}
            downloadsDescription="Technical PDFs and documents for this board."
          />
        </section>



        <BoardsDetailDisclaimer />

        <BoardsDetailExploreSlider />
        <AllSolutionsRequestNewProject />
      </div>
      <Footer />
    </div>
  );
};

export default BoardsDetail;
